/**
 * Shows its content only when the store is currently open.
 *
 * @example
 * <store-status timezone="America/Detroit" hours='[null,["10:00","17:00"],["10:00","17:00"]]'>
 *   We're open
 * </store-status>
 *
 * Hours: array of 7 entries (Sun–Sat). null or [] = closed. ["HH:MM","HH:MM"] = open/close (24h, close exclusive).
 */
class StoreStatus extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #timeoutId = null;

  /** @type {Intl.DateTimeFormat} */
  #timeFormatter;

  /** @type {Intl.DateTimeFormat} */
  #weekdayFormatter;

  connectedCallback() {
    this.#timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: this.#timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });

    this.#weekdayFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: this.#timezone,
      weekday: 'short',
    });

    this.#update();
  }

  disconnectedCallback() {
    if (this.#timeoutId !== null) {
      clearTimeout(this.#timeoutId);
      this.#timeoutId = null;
    }
  }

  get #timezone() {
    return this.getAttribute('timezone') || 'UTC';
  }

  /** @returns {Array<[string, string] | null>} */
  #parseHours() {
    try {
      const parsed = JSON.parse(this.getAttribute('hours') || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** @param {string} time */
  #toMinutes(time) {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  /** @returns {{ day: number, minutes: number, seconds: number }} */
  #now() {
    const date = new Date();
    const weekday = this.#weekdayFormatter.format(date);
    const day = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[weekday] ?? 0;
    const parts = this.#timeFormatter.formatToParts(date);
    const get = (/** @type {string} */ type) =>
      Number(parts.find((part) => part.type === type)?.value ?? 0);

    return {
      day,
      minutes: get('hour') * 60 + get('minute'),
      seconds: get('second'),
    };
  }

  /**
   * @param {number} minutes
   * @param {[string, string]} range
   */
  #isWithin(minutes, range) {
    return minutes >= this.#toMinutes(range[0]) && minutes < this.#toMinutes(range[1]);
  }

  /** @param {Array<[string, string] | null>} hours */
  #isOpen(hours) {
    const { day, minutes } = this.#now();
    const today = hours[day];
    return Array.isArray(today) && today.length === 2 && this.#isWithin(minutes, today);
  }

  /**
   * @param {Array<[string, string] | null>} hours
   * @returns {number | null}
   */
  #msUntilChange(hours) {
    const { day, minutes, seconds } = this.#now();
    const today = hours[day];
    const openNow =
      Array.isArray(today) && today.length === 2 && this.#isWithin(minutes, today);

    if (openNow) {
      const closeMinutes = this.#toMinutes(today[1]);
      return Math.max((closeMinutes - minutes) * 60 - seconds, 1) * 1000;
    }

    if (Array.isArray(today) && today.length === 2) {
      const openMinutes = this.#toMinutes(today[0]);
      if (minutes < openMinutes) {
        return Math.max((openMinutes - minutes) * 60 - seconds, 1) * 1000;
      }
    }

    for (let offset = 1; offset <= 7; offset++) {
      const nextDay = hours[(day + offset) % 7];
      if (Array.isArray(nextDay) && nextDay.length === 2) {
        const openMinutes = this.#toMinutes(nextDay[0]);
        const minutesUntilOpen = offset * 1440 - minutes + openMinutes;
        return Math.max(minutesUntilOpen * 60 - seconds, 1) * 1000;
      }
    }

    return null;
  }

  #update() {
    if (this.#timeoutId !== null) {
      clearTimeout(this.#timeoutId);
      this.#timeoutId = null;
    }

    const hours = this.#parseHours();
    this.hidden = !this.#isOpen(hours);

    const delay = this.#msUntilChange(hours);
    if (delay !== null) {
      this.#timeoutId = setTimeout(() => this.#update(), delay);
    }
  }
}

if (!customElements.get('store-status')) {
  customElements.define('store-status', StoreStatus);
}
